import React, { useEffect, useMemo } from 'react';
import { useDispatchNoteList } from '../hooks/use-dispatch-note-list';
import {
  APP_TYPE,
  dispatch,
  DispatchList,
  DispatchNoteStatus,
  getAppType,
  getSearchKey,
  getSelectedItemIds,
  getSendType,
  updateExtraDetails,
  useAppSelector,
} from '@zonofi/zono-money-store';
import {
  ChipFilters,
  CommonButton,
  EmptyStateView,
  EndListView,
  ListSkeleton,
  PullRefreshContainer,
  SpinnerLoader,
  searchComponents,
} from '../../components';
import { isEmpty } from 'lodash';
import { SVGS } from '../../assets';
import { DispatchNoteCard } from '../dispatch-note-card';
import './dispatch-note-list-content.scss';
import {
  FIELD_OPS_ROUTES,
  SearchConfig,
  SELLER_WEBAPP_ROUTES,
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import { ChipFilterSection } from '../../types';
import { getTextFromRange } from '../../helper';

interface DispatchNoteListContentProps {
  setDispatchNoteData?: React.Dispatch<React.SetStateAction<DispatchList[]>>;
  searchConfig?: SearchConfig;
  setShowSearch?: (val: boolean) => void;
  showSearch?: boolean;
  onDateChipPress?: () => void;
  isCustomer?: boolean;
}
export const DispatchNoteListContent: React.FC<
  DispatchNoteListContentProps
> = ({
  setDispatchNoteData = () => {},
  searchConfig = {} as SearchConfig,
  setShowSearch = () => {},
  showSearch = false,
  onDateChipPress = () => {},
  isCustomer = false,
}) => {
  const navigate = useNavigate();
  const {
    dispatchNoteListData,
    isEndOfList,
    lastItemRef,
    onScroll,
    isLoading,
    onSearchCancel,
    statusFilters,
    setAppliedStatus,
    appliedDateRange,
    refreshDispatchNoteList,
  } = useDispatchNoteList({ isCustomer });

  const apptype = useAppSelector(getAppType);
  const searchKey = useAppSelector(getSearchKey);
  const sendType = useAppSelector(getSendType);
  const selectedDispatchIds = useAppSelector(getSelectedItemIds);

  const searchProp =
    apptype === APP_TYPE.FIELDS_OPS
      ? { search: searchComponents({ searchConfig, setShowSearch }) }
      : undefined;

  useEffect(() => {
    setDispatchNoteData(dispatchNoteListData ?? []);
  }, [dispatchNoteListData]);

  const onContinuePress = () => {
    const routeToNavigate =
      apptype === APP_TYPE.SELLER_HUB ? SELLER_WEBAPP_ROUTES : FIELD_OPS_ROUTES;
    navigate(routeToNavigate.VIEW_SELECTED_DISPATCH_NOTES);
  };

  const onDispatchNotePress = (dispatchNote: DispatchList) => {
    if (!dispatchNote?.id) return;
    const {
      customerName = '',
      inviteId = '',
      id,
      dispatchDocNumber = '',
      status = '',
    } = dispatchNote;

    dispatch(
      updateExtraDetails({
        customerName: customerName ?? '',
        customerId: inviteId ?? '',
      })
    );
    const navigationPath =
      apptype === APP_TYPE.FIELDS_OPS ? FIELD_OPS_ROUTES : SELLER_WEBAPP_ROUTES;
    navigate(
      `${navigationPath.DISPATCH_NOTE_DETAILS.replace(':dispatchNoteId', id)}?dispatchDocNo=${dispatchDocNumber}&dispatchStatus=${status}`
    );
  };

  const chips: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: '',
            value: getTextFromRange(appliedDateRange),
            selected: true,
            type: 'date',
          },
        ],
        onItemPress: onDateChipPress,
      },
      {
        items: statusFilters,
        onItemPress: ({ id, value }) =>
          setAppliedStatus(value as DispatchNoteStatus),
      },
    ],
    [appliedDateRange, statusFilters]
  );

  return (
    <>
      <div className="dispatch-note-list-content">
        {chips && (
          <ChipFilters
            sections={chips}
            chipFilterStyles={{
              marginTop: '-12px',
              alignSelf: 'flex-start',
              width: '100%',
              position: 'sticky',
              top: '-16px',
              left: '-16px',
              ...(apptype === APP_TYPE.FIELDS_OPS &&
                searchConfig?.enabled && { padding: 0 }),
            }}
            showSearch={showSearch}
            {...(searchProp && searchProp)}
          />
        )}
        <PullRefreshContainer
          className="dispatch-note-list-content-container"
          onScroll={onScroll}
          onRefresh={refreshDispatchNoteList}
        >
          {!searchConfig?.enabled && searchKey.length > 0 && (
            <div className="dispatch-note-search-info font-regular">
              <span className="">
                RESULTS FOR{' '}
                <span className="font-semibold">{`"${searchKey}"`}</span>
              </span>
              <span
                className="clear-btn font-semibold"
                onClick={onSearchCancel}
              >
                CLEAR
              </span>
            </div>
          )}
          {isLoading ? (
            <ListSkeleton className="dispatch-note-list-screen-skeleton" />
          ) : isEmpty(dispatchNoteListData) ? (
            <EmptyStateView
              imageSrc={SVGS.NoDispatchNote}
              message={'No Dispatch Notes Found'}
            />
          ) : (
            <div className="dispatch-note-list-view">
              {dispatchNoteListData?.map((dispatchNote) => (
                <DispatchNoteCard
                  key={dispatchNote?.id}
                  item={dispatchNote}
                  onDeletePress={() => {}}
                  isDisable={false}
                  showDeletion={false}
                  onDispatchNotePress={() => onDispatchNotePress(dispatchNote)}
                  showSelection={sendType?.length !== 0}
                />
              ))}
              <div ref={lastItemRef} className="dispatch-note-last-item" />
              {!isEndOfList && <SpinnerLoader />}
              {isEndOfList && <EndListView />}
            </div>
          )}
        </PullRefreshContainer>
      </div>
      {selectedDispatchIds?.length > 0 && (
        <div className="continue-bottom-button">
          <CommonButton
            text="View Selected Dispatch Notes"
            onPress={onContinuePress}
          />
        </div>
      )}
    </>
  );
};
